import * as THREE from 'three';
import { CarnivalScene } from '../art';
import { AimController } from '../core/AimController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { HeldBall } from '../entities/HeldBall';
import { CupPhysicsSystem } from '../physics';
import { AimGuide } from '../systems/AimGuide';
import { AudioSystem } from '../systems/AudioSystem';
import { Hud } from '../systems/Hud';
import { GAME_CONFIG } from './config';
import { solveThrow, type ThrowSolution } from './trajectory';
import type { GamePhase, HudState, PhysicsFrame, ThrowOutcome } from './types';

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(44, 1, 0.1, 110);
  private readonly physics = new CupPhysicsSystem();
  private readonly carnival = new CarnivalScene();
  private readonly audio = new AudioSystem();
  private readonly aimGuide = new AimGuide();
  private readonly heldBall = new HeldBall();
  private readonly hud: Hud;
  private readonly aim: AimController;
  private readonly loop: Loop;
  private readonly releasePosition = new THREE.Vector3(
    GAME_CONFIG.releasePosition.x,
    GAME_CONFIG.releasePosition.y,
    GAME_CONFIG.releasePosition.z,
  );
  private readonly throwOrigin = this.releasePosition.clone();
  private readonly aimPoint = new THREE.Vector3();
  private readonly calibratedTarget = new THREE.Vector3();
  private readonly aimDirection = new THREE.Vector3();
  private readonly ballPosition = new THREE.Vector3();
  private readonly cameraBase = new THREE.Vector3();
  private readonly cameraTarget = new THREE.Vector3();
  private readonly cameraShake = new THREE.Vector3();
  private readonly throwSolution: ThrowSolution = {
    velocity: new THREE.Vector3(),
    flightTime: 1,
  };
  private readonly artDiagnostics = this.carnival.getDiagnostics();

  private frame = 0;
  private elapsed = 0;
  private smoothedFrameMs = 16.67;
  private diagnosticsElapsed = 0;
  private phase: GamePhase = 'ready';
  private ballsRemaining = GAME_CONFIG.roundBalls;
  private score = 0;
  private specialHits = 0;
  private currentPower = 0;
  private resultElapsed = 0;
  private cameraImpulse = 0;
  private lastOutcome: ThrowOutcome | null = null;
  private initialized = false;
  private dprCap = 1.75;

  private constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.hud = new Hud({
      onRestart: () => this.restartRound(),
      onToggleMute: () => this.audio.toggleMuted(),
    });
    this.aim = new AimController(canvas, this.camera, (point, power) => this.releaseThrow(point, power));
    this.loop = new Loop(
      (delta, elapsed) => this.update(delta, elapsed),
      () => this.render(),
    );
  }

  static async create(canvas: HTMLCanvasElement): Promise<Game> {
    const game = new Game(canvas);
    await game.initialize();
    return game;
  }

  start(): void {
    if (!this.initialized) throw new Error('Game must finish initializing before start().');
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.aim.dispose();
    this.hud.dispose();
    this.audio.dispose();
    this.physics.dispose();
    this.carnival.dispose();
    this.aimGuide.dispose();
    this.heldBall.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__CUP_GAME_TEST__ = undefined;
  }

  private async initialize(): Promise<void> {
    this.configureScene();
    await this.physics.initialize();
    this.scene.add(this.carnival.group, this.physics.group, this.aimGuide.group, this.heldBall.group);
    this.updateCameraFraming(true);
    this.aim.copyAimPoint(this.aimPoint);
    this.refreshAimGuide(0.48);
    this.updateHud();
    this.initialized = true;
    this.publishDiagnostics();
    this.installTestApi();
  }

  private configureScene(): void {
    this.scene.background = new THREE.Color('#071427');
    this.scene.fog = new THREE.Fog('#071427', 20, 58);

    const hemisphere = new THREE.HemisphereLight('#c9dcff', '#5b281f', 1.75);
    this.scene.add(hemisphere);

    const key = new THREE.DirectionalLight('#fff0c4', 3.25);
    key.position.set(-5.5, 14, 9.5);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 2;
    key.shadow.camera.far = 38;
    key.shadow.camera.left = -8;
    key.shadow.camera.right = 8;
    key.shadow.camera.top = 9;
    key.shadow.camera.bottom = -5;
    key.shadow.bias = -0.00035;
    this.scene.add(key);

    const pinkFill = new THREE.PointLight('#ff4770', 18, 19, 2);
    pinkFill.position.set(-6.3, 6.2, -1.5);
    const blueFill = new THREE.PointLight('#38d4ff', 14, 18, 2);
    blueFill.position.set(6.4, 5.1, -5.2);
    this.scene.add(pinkFill, blueFill);
  }

  private update(delta: number, _elapsed: number): void {
    this.frame += 1;
    this.smoothedFrameMs = THREE.MathUtils.lerp(this.smoothedFrameMs, delta * 1000, 0.075);
    this.diagnosticsElapsed += delta;
    if (this.phase !== 'round-over') this.elapsed += delta;

    this.dprCap = this.getResponsiveDprCap();
    const resized = resizeRenderer(this.renderer, this.camera, this.dprCap);
    if (resized) this.updateCameraFraming(false);

    this.heldBall.group.visible = this.phase === 'ready' || this.phase === 'charging';
    this.heldBall.update(delta, this.currentPower, this.phase === 'charging');
    this.throwOrigin.copy(this.heldBall.group.position);

    if (this.phase === 'ready' || this.phase === 'charging') {
      this.currentPower = this.aim.getPower();
      this.phase = this.aim.isCharging() ? 'charging' : 'ready';
      this.aim.copyAimPoint(this.aimPoint);
      this.refreshAimGuide(this.aim.isCharging() ? Math.max(0.08, this.currentPower) : 0.48);
      this.carnival.pulseAim(this.aimPoint, this.currentPower);
    }

    const physicsFrame = this.physics.step(delta);
    this.handlePhysicsFrame(physicsFrame);

    if (this.phase === 'resolving') {
      this.resultElapsed += delta;
      if (this.resultElapsed >= GAME_CONFIG.resultHoldDuration) this.advanceAfterResult();
    }

    this.aimGuide.update(delta);
    this.carnival.update(delta, this.elapsed);
    this.updateCamera(delta);
    this.updateHud();
    if (this.diagnosticsElapsed >= 0.2) {
      this.diagnosticsElapsed = 0;
      this.publishDiagnostics();
    }
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private releaseThrow(point: THREE.Vector3, power: number): void {
    if ((this.phase !== 'ready' && this.phase !== 'charging') || this.ballsRemaining <= 0) return;
    this.aim.setAimPoint(point);
    this.aim.copyAimPoint(this.aimPoint);
    this.throwOrigin.copy(this.heldBall.group.position);
    this.solveCalibratedThrow(power);
    this.physics.launchBall(this.throwOrigin, this.throwSolution.velocity);
    this.ballsRemaining -= 1;
    this.phase = 'in-flight';
    this.currentPower = power;
    this.lastOutcome = null;
    this.resultElapsed = 0;
    this.aim.setEnabled(false);
    this.aimGuide.hide();
    this.audio.throw(power);
    this.cameraImpulse = 0.035 + power * 0.035;
  }

  private refreshAimGuide(power: number): void {
    this.solveCalibratedThrow(power);
    this.aimGuide.show(
      this.throwOrigin,
      this.throwSolution.velocity,
      this.throwSolution.flightTime,
      this.aimPoint,
      power,
    );
  }

  private solveCalibratedThrow(power: number): void {
    this.aimDirection.copy(this.aimPoint).sub(this.throwOrigin);
    this.aimDirection.y = 0;
    if (this.aimDirection.lengthSq() > 0.0001) this.aimDirection.normalize();
    this.calibratedTarget
      .copy(this.aimPoint)
      .addScaledVector(this.aimDirection, GAME_CONFIG.entryLead);
    solveThrow(this.throwOrigin, this.calibratedTarget, power, this.throwSolution);
  }

  private handlePhysicsFrame(frame: PhysicsFrame): void {
    for (const impact of frame.impacts) {
      if (impact.surface === 'board') this.audio.boardHit(impact.speed);
      else this.audio.rimHit(impact.speed);
      this.carnival.impact(impact.position, THREE.MathUtils.clamp(impact.speed / 10, 0.25, 1));
      this.cameraImpulse = Math.max(this.cameraImpulse, Math.min(0.085, impact.speed * 0.006));
    }

    if (frame.outcome && this.phase === 'in-flight') this.resolveOutcome(frame.outcome);
  }

  private resolveOutcome(outcome: ThrowOutcome): void {
    this.phase = 'resolving';
    this.lastOutcome = outcome;
    this.resultElapsed = 0;
    this.hud.announceOutcome(outcome);

    if (outcome.kind === 'special' && outcome.cup) {
      this.score += outcome.scoreAwarded;
      const newlyClaimed = outcome.scoreAwarded > 0;
      if (newlyClaimed) {
        this.specialHits += 1;
        this.physics.markCupClaimed(outcome.cup.index);
      }
      this.carnival.celebrate(outcome.cup.center, newlyClaimed);
      this.audio.specialCup();
      this.cameraImpulse = newlyClaimed ? 0.13 : 0.065;
    } else if (outcome.kind === 'normal' && outcome.cup) {
      this.carnival.celebrate(outcome.cup.center, false);
      this.audio.normalCup();
      this.cameraImpulse = 0.055;
    } else {
      this.audio.miss();
    }
  }

  private advanceAfterResult(): void {
    this.physics.removeBall();
    if (this.ballsRemaining <= 0) {
      this.phase = 'round-over';
      this.aim.setEnabled(false);
      this.aimGuide.hide();
      this.carnival.setRoundOver(true);
      this.audio.roundOver(this.specialHits);
      return;
    }

    this.phase = 'ready';
    this.currentPower = 0;
    this.lastOutcome = null;
    this.aim.setEnabled(true);
    this.aim.copyAimPoint(this.aimPoint);
    this.refreshAimGuide(0.48);
  }

  private restartRound(): void {
    this.audio.uiClick();
    this.physics.resetRound();
    this.carnival.setRoundOver(false);
    this.phase = 'ready';
    this.ballsRemaining = GAME_CONFIG.roundBalls;
    this.score = 0;
    this.specialHits = 0;
    this.elapsed = 0;
    this.resultElapsed = 0;
    this.currentPower = 0;
    this.lastOutcome = null;
    this.cameraImpulse = 0;
    this.aim.cancel();
    this.aim.setEnabled(true);
    this.aim.copyAimPoint(this.aimPoint);
    this.refreshAimGuide(0.48);
    this.updateHud();
  }

  private updateCameraFraming(snap: boolean): void {
    const height = Math.max(1, this.canvas.clientHeight);
    const aspect = Math.max(0.35, this.canvas.clientWidth / height);
    const narrow = aspect < 0.82;
    const shortLandscape = aspect >= 1.25 && height <= 500;
    this.camera.fov = narrow ? 60 : shortLandscape ? 33 : aspect < 1.25 ? 49 : 44;
    this.camera.near = 0.1;
    this.camera.far = 110;
    this.camera.updateProjectionMatrix();

    this.cameraBase.set(
      GAME_CONFIG.cameraPosition.x,
      GAME_CONFIG.cameraPosition.y + (narrow ? 0.65 : 0),
      GAME_CONFIG.cameraPosition.z + (narrow ? 2.15 : shortLandscape ? 4.6 : 0),
    );
    this.cameraTarget.set(
      GAME_CONFIG.cameraTarget.x,
      GAME_CONFIG.cameraTarget.y + (shortLandscape ? -0.12 : 0),
      GAME_CONFIG.cameraTarget.z + (narrow ? 0.35 : 0),
    );
    if (snap) {
      this.camera.position.copy(this.cameraBase);
      this.camera.lookAt(this.cameraTarget);
    }
  }

  private updateCamera(delta: number): void {
    this.cameraImpulse *= Math.exp(-delta * 8.5);
    this.cameraShake.set(
      Math.sin(this.frame * 2.31) * this.cameraImpulse,
      Math.sin(this.frame * 1.73 + 0.8) * this.cameraImpulse * 0.65,
      0,
    );
    this.camera.position.copy(this.cameraBase).add(this.cameraShake);
    this.camera.lookAt(this.cameraTarget);
  }

  private getResponsiveDprCap(): number {
    const shortEdge = Math.min(this.canvas.clientWidth, this.canvas.clientHeight);
    return shortEdge <= 540 ? 1.5 : 1.75;
  }

  private updateHud(): void {
    const state: HudState = {
      phase: this.phase,
      ballsRemaining: this.ballsRemaining,
      score: this.score,
      specialHits: this.specialHits,
      totalSpecial: GAME_CONFIG.specialCupIndices.length,
      power: this.currentPower,
      status: this.statusText(),
      lastOutcome: this.lastOutcome,
      muted: this.audio.isMuted(),
    };
    this.hud.update(state);
  }

  private statusText(): string {
    if (this.phase === 'ready') return 'Aim for a red prize cup';
    if (this.phase === 'charging') return 'Hold steady · release to toss';
    if (this.phase === 'in-flight') return 'Watch the bounce…';
    if (this.phase === 'round-over') {
      return this.specialHits > 0 ? 'Prize run complete!' : 'The prize cups are waiting';
    }
    if (this.lastOutcome?.kind === 'special' && this.lastOutcome.scoreAwarded > 0) {
      return `Prize cup! +${this.lastOutcome.scoreAwarded} tickets`;
    }
    if (this.lastOutcome?.kind === 'special') return 'That prize cup is already claimed';
    if (this.lastOutcome?.kind === 'normal') return 'Clean landing · prize cups score';
    return 'So close · line up the next toss';
  }

  private publishDiagnostics(): void {
    this.aim.copyAimPoint(this.aimPoint);
    const activeBall = this.physics.getBallPosition(this.ballPosition);
    const rendererInfo = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      phase: this.phase,
      ballsRemaining: this.ballsRemaining,
      score: this.score,
      specialHits: this.specialHits,
      power: this.currentPower,
      aim: { x: this.aimPoint.x, y: this.aimPoint.y, z: this.aimPoint.z },
      ball: activeBall ? { x: activeBall.x, y: activeBall.y, z: activeBall.z } : null,
      renderer: {
        calls: rendererInfo.render.calls,
        triangles: rendererInfo.render.triangles,
        geometries: rendererInfo.memory.geometries,
        textures: rendererInfo.memory.textures,
        frameMs: this.smoothedFrameMs,
        fps: 1000 / Math.max(0.01, this.smoothedFrameMs),
      },
      physics: this.physics.getDiagnostics(),
      art: this.artDiagnostics,
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: this.renderer.getPixelRatio(),
      },
    };
  }

  private installTestApi(): void {
    if (!import.meta.env.DEV) return;
    window.__CUP_GAME_TEST__ = {
      getState: () => window.__THREE_GAME_DIAGNOSTICS__,
      aimAtCup: (index: number) => {
        const cup = this.physics.cups[index];
        if (!cup) return false;
        this.aim.setAimPoint(cup.center);
        this.aim.copyAimPoint(this.aimPoint);
        this.refreshAimGuide(0.62);
        return true;
      },
      throwAtCup: (index: number, power = 0.62) => {
        const cup = this.physics.cups[index];
        if (!cup || (this.phase !== 'ready' && this.phase !== 'charging')) return false;
        this.aim.setAimPoint(cup.center);
        this.releaseThrow(cup.center, THREE.MathUtils.clamp(power, 0.08, 1));
        return true;
      },
      advance: (seconds: number) => {
        const steps = Math.ceil(THREE.MathUtils.clamp(seconds, 0, 12) * 60);
        for (let step = 0; step < steps; step += 1) this.update(1 / 60, 0);
        this.render();
        this.publishDiagnostics();
      },
      restart: () => this.restartRound(),
    };
  }
}
